import { Barcode, CalendarDays, FileText, Hash, Receipt, Table2 } from 'lucide-react';
import type { BarcodeResult, OcrLine } from '../shared/desktop-api';
import { spotPage, symbologyName, type ReadingFacts, type SpotKind } from './working-progress';

/** "PURCHASE ORDER" reads as "Purchase order" in a list. */
const titleCase = (text: string) =>
  text.charAt(0).toLocaleUpperCase() + text.slice(1).toLocaleLowerCase();
const spotIcons: Record<SpotKind, typeof Barcode> = {
  title: FileText,
  reference: Hash,
  date: CalendarDays,
  total: Receipt,
};

/**
 * The Read step's side of the screen: what the reader has found, in the terms a
 * person thinks about their paperwork. A few running counts (tables, reference
 * numbers, barcodes, pages straightened or set aside), then what is on the page on
 * screen: what it looks like, its numbers, dates and total, its tables and codes,
 * the same things outlined on the page itself (owner, 2026-09-28: word counts mean
 * nothing to anyone).
 */
export function ReadingFinds({
  reading,
  barcodeCount,
  pageLines,
  pageBarcodes,
}: {
  reading: ReadingFacts;
  /** The engine's own running total, exact even for a screen that came back mid-run. */
  barcodeCount: number;
  /** The reader's lines and codes for the page on screen. */
  pageLines: readonly OcrLine[];
  pageBarcodes: readonly BarcodeResult[];
}) {
  const totals = [
    // A screen that came back mid-run saw only part of the pages, so it states no running counts of its own.
    ...(reading.fromStart
      ? [
          { label: 'Tables', value: reading.tables },
          { label: 'Reference numbers', value: reading.references },
        ]
      : []),
    { label: 'Barcodes', value: barcodeCount },
    ...(reading.straightened > 0 ? [{ label: 'Straightened', value: reading.straightened }] : []),
    ...(reading.blank > 0 ? [{ label: 'Blank pages', value: reading.blank }] : []),
  ];
  const page = spotPage(pageLines);
  const cards = [
    ...page.spots.map((spot, index) => ({
      key: `spot:${index}`,
      kind: spot.kind,
      Icon: spotIcons[spot.kind],
      label: spot.label,
      value: spot.value,
    })),
    ...page.tables.map((table, index) => ({
      key: `table:${index}`,
      kind: 'table',
      Icon: Table2,
      label: 'Table',
      value: `${table.rows.length.toLocaleString()} rows`,
    })),
    ...pageBarcodes
      .filter((code) => code.value?.trim())
      .slice(0, 2)
      .map((code, index) => ({
        key: `code:${index}`,
        kind: 'barcode',
        Icon: Barcode,
        label: symbologyName(code.symbology),
        value: code.value.trim(),
      })),
  ].slice(0, 8);
  return (
    <section className="reading-finds" aria-label="Found while reading">
      <dl className="reading-totals">
        {totals.map((total) => (
          <div key={total.label}>
            <dt>{total.label}</dt>
            <dd>{total.value.toLocaleString()}</dd>
          </div>
        ))}
      </dl>
      {cards.length > 0 && (
        <div className="reading-on-page">
          <h3>On this page</h3>
          <ul aria-label="On this page">
            {cards.map(({ key, kind, Icon, label, value }) => (
              <li key={key} className={`reading-spot reading-spot-${kind}`}>
                <Icon size={15} aria-hidden="true" />
                <span>
                  <small>{label}</small>
                  <strong title={value}>{value}</strong>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {reading.recentPages.length > 1 && (
        <div className="reading-recent">
          <h3>Just read</h3>
          <ol>
            {reading.recentPages.map((page) => (
              <li key={page.id}>
                <FileText size={14} aria-hidden="true" />
                <span>{page.title ? titleCase(page.title) : 'Page'}</span>
                {page.lead && (
                  <small title={`${page.lead.label} ${page.lead.value}`}>
                    {titleCase(page.lead.label)} <b>{page.lead.value}</b>
                  </small>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
